import { useState, useEffect } from "react";
import { useAdminAuth } from "./AdminAuthContext";
import { AdminEmailNotConfiguredNotice } from "./AdminEmailNotConfiguredNotice";
import { fetchWholesaleInquiries } from "../supabase";
import { Eye, X } from "lucide-react";

type InquiryStatus = "new" | "contacted" | "in_progress" | "closed" | "declined";

interface WholesaleInquiry {
  id: number;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string | null;
  estimated_volume: string | null;
  message: string | null;
  partnership_tier: string | null;
  status: InquiryStatus;
  internal_notes: string | null;
  created_at: string;
  updated_at: string;
}

const statusColors: Record<InquiryStatus, { bg: string; text: string }> = {
  new: { bg: "#C7A76C15", text: "#C7A76C" },
  contacted: { bg: "#6F7F6315", text: "#6F7F63" },
  in_progress: { bg: "#8A6D6315", text: "#8A6D63" },
  closed: { bg: "#74856D15", text: "#74856D" },
  declined: { bg: "#8B5C5815", text: "#8B5C58" },
};

export function AdminWholesaleInquiries() {
  const { hasPermission } = useAdminAuth();
  const [inquiries, setInquiries] = useState<WholesaleInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<WholesaleInquiry | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchWholesaleInquiries()
      .then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) {
          setError(err.message);
          setInquiries([]);
          return;
        }
        setError(null);
        setInquiries((data ?? []) as WholesaleInquiry[]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (!hasPermission("wholesale_inquiries")) {
    return (
      <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>
        You don&apos;t have access to this section.
      </p>
    );
  }

  return (
    <div>
      <h1
        className="text-[#2D2A26] tracking-[0.08em] mb-8"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
      >
        Wholesale Inquiries
      </h1>
      <AdminEmailNotConfiguredNotice />

      {error && (
        <p className="text-[#8B5C58] mb-4" style={{ fontSize: "13px" }}>
          {error}
        </p>
      )}

      {loading ? (
        <div className="bg-white rounded-xl p-12 shadow-[0_2px_16px_rgba(40,32,20,0.04)] text-center">
          <p className="text-[#D8D1C7]" style={{ fontSize: "14px" }}>
            Loading…
          </p>
        </div>
      ) : inquiries.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-[0_2px_16px_rgba(40,32,20,0.04)] text-center">
          <p className="text-[#D8D1C7]" style={{ fontSize: "14px" }}>
            No wholesale inquiries yet. Submissions from the wholesale form will appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E6E0D7]">
                  <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>
                    Company
                  </th>
                  <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase hidden md:table-cell" style={{ fontSize: "10px", fontWeight: 400 }}>
                    Contact
                  </th>
                  <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase hidden lg:table-cell" style={{ fontSize: "10px", fontWeight: 400 }}>
                    Email
                  </th>
                  <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase hidden sm:table-cell" style={{ fontSize: "10px", fontWeight: 400 }}>
                    Volume
                  </th>
                  <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase hidden md:table-cell" style={{ fontSize: "10px", fontWeight: 400 }}>
                    Tier
                  </th>
                  <th className="text-center px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>
                    Status
                  </th>
                  <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase hidden lg:table-cell" style={{ fontSize: "10px", fontWeight: 400 }}>
                    Date
                  </th>
                  <th className="text-right px-5 py-3" style={{ width: "80px" }} />
                </tr>
              </thead>
              <tbody>
                {inquiries.map((inq) => {
                  const colors = statusColors[inq.status] ?? statusColors.new;
                  return (
                    <tr
                      key={inq.id}
                      className="border-b border-[#E6E0D7]/50 hover:bg-[#F2EEE8]/50 transition-colors"
                    >
                      <td className="px-5 py-3">
                        <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                          {inq.company_name}
                        </p>
                      </td>
                      <td className="px-5 py-3 hidden md:table-cell">
                        <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                          {inq.contact_name}
                        </p>
                      </td>
                      <td className="px-5 py-3 hidden lg:table-cell">
                        <a
                          href={`mailto:${inq.email}`}
                          className="text-[#6F7F63] hover:underline"
                          style={{ fontSize: "12px" }}
                        >
                          {inq.email}
                        </a>
                      </td>
                      <td className="px-5 py-3 hidden sm:table-cell text-[#D8D1C7]" style={{ fontSize: "12px" }}>
                        {inq.estimated_volume ?? "—"}
                      </td>
                      <td className="px-5 py-3 hidden md:table-cell text-[#2D2A26]" style={{ fontSize: "12px" }}>
                        {inq.partnership_tier ?? "—"}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <span
                          className="inline-block px-2.5 py-1 rounded-full"
                          style={{
                            fontSize: "10px",
                            fontWeight: 400,
                            background: colors.bg,
                            color: colors.text,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                          }}
                        >
                          {inq.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-5 py-3 hidden lg:table-cell text-[#D8D1C7]" style={{ fontSize: "12px" }}>
                        {new Date(inq.created_at).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setViewing(inq)}
                          className="p-1.5 text-[#6F675F] hover:text-[#6F7F63] transition-colors"
                          aria-label="View details"
                        >
                          <Eye size={14} strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E0D7] shrink-0">
              <h2
                className="text-[#2D2A26] truncate pr-4"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}
              >
                {viewing.company_name}
              </h2>
              <button
                type="button"
                onClick={() => setViewing(null)}
                className="text-[#D8D1C7] hover:text-[#2D2A26] transition-colors shrink-0"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>
                    Contact
                  </p>
                  <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                    {viewing.contact_name}
                  </p>
                </div>
                <div>
                  <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>
                    Email
                  </p>
                  <a
                    href={`mailto:${viewing.email}`}
                    className="text-[#6F7F63] hover:underline"
                    style={{ fontSize: "13px" }}
                  >
                    {viewing.email}
                  </a>
                </div>
                {viewing.phone && (
                  <div>
                    <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>
                      Phone
                    </p>
                    <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                      {viewing.phone}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>
                    Estimated volume
                  </p>
                  <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                    {viewing.estimated_volume ?? "—"}
                  </p>
                </div>
                {viewing.partnership_tier && (
                  <div>
                    <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>
                      Partnership tier
                    </p>
                    <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                      {viewing.partnership_tier}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>
                  Message
                </p>
                <p
                  className="text-[#2D2A26] whitespace-pre-wrap"
                  style={{ fontSize: "13px", fontWeight: 300 }}
                >
                  {viewing.message || "—"}
                </p>
              </div>
              <div className="border-t border-[#E6E0D7] pt-4 flex justify-between text-[#D8D1C7]" style={{ fontSize: "11px" }}>
                <span>Submitted {new Date(viewing.created_at).toLocaleString()}</span>
                <span className="capitalize">{viewing.status.replace("_", " ")}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
